
import Header from "@/components/Header";
import { CreateCoinForm } from "@/components/create-coin/CreateCoinForm";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Users, Lock } from "lucide-react";
import { useCheckDAOMembership } from "@/hooks/useCheckDAOMembership";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ExistingCoinFairLaunchForm } from "@/components/create-coin/ExistingCoinFairLaunchForm";

const CreateCoin = () => {
  const { isDAOMember, isLoading, userDAOs } = useCheckDAOMembership();
  const navigate = useNavigate();

  // If not a DAO member, show gated message
  if (!isLoading && !isDAOMember) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-2xl mx-auto">
            <Card className="border-destructive/50">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Lock className="h-6 w-6 text-destructive" />
                  <CardTitle>DAO Membership Required</CardTitle>
                </div>
                <CardDescription>
                  To ensure quality and governance, only DAO members can launch tokens on this platform.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Why This Requirement?</AlertTitle>
                  <AlertDescription>
                    We require all token creators to have a DAO to ensure proper governance, 
                    transparency, and community participation. This prevents single-person "fake" DAOs 
                    and ensures every token has real governance structure.
                  </AlertDescription>
                </Alert>
                
                <div className="flex flex-col gap-2">
                  <Button onClick={() => navigate('/dao-it')} size="lg">
                    Create Your DAO First
                  </Button>
                  <Button onClick={() => navigate('/')} variant="outline">
                    Go Back Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // If loading, show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 pt-24 pb-16">
          <div className="max-w-2xl mx-auto text-center">
            <p>Checking DAO membership...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold mb-4">Create Social Token</h1>
          
          <Alert variant="default" className="mb-4 bg-green-500/10 border-green-500/20">
            <Users className="h-4 w-4 text-green-500" />
            <AlertTitle>DAO Member Verified</AlertTitle>
            <AlertDescription>
              You're a member of {userDAOs.length} DAO{userDAOs.length > 1 ? 's' : ''}. 
              Select which DAO will govern this token.
            </AlertDescription>
          </Alert>
          
          <Alert variant="default" className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Important</AlertTitle>
            <AlertDescription>
              Creating a social token requires MATIC for gas fees. Your token will enable community 
              engagement, rewards for supporters, and new ways to connect with your audience.
            </AlertDescription>
          </Alert>
          
          <CreateCoinForm />
          <ExistingCoinFairLaunchForm />
        </div>
      </main>
    </div>
  );
};

export default CreateCoin;
